import { createApi } from "@reduxjs/toolkit/query/react";

import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_SIGN_UP,
  AUTH_RESEND_OTP,
  AUTH_RESET_PASSWORD,
  AUTH_ACTIVE_SESSIONS,
  AUTH_FORGOT_PASSWORD,
  AUTH_VERIFY_RESET_PASSWORD_OTP,
  AUTH_VERIFY_EMAIL,
  INITIATE_MFA_SETUP,
  VERIFY_MFA_CODE,
  MFA_STATUS,
  DISABLE_MFA,
  AUTH_VERIFY_MFA_LOGIN,
  AUTH_LOGIN_HISTORY,
  AUTH_TERMINATE,
  AUTH_TERMINATE_OTHERS,
  AUTH_EXCHANGE_IMPERSONATION_CODE,
  AUTH_END_IMPERSONATION,
  AUTH_VALIDATE_RESET_TOKEN,
} from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IDeviceData,
  IInitiateMFAResponse,
  ILoginPayload,
  ILoginResponse,
  ILoginWithMFAResponse,
  IMFAStatusResponse,
  ISignUp,
  ISignupVerifyEmailResponse,
  IVerifyEmail,
  IVerifyMFAPayload,
  TVerifyandEnableMFAResponse,
} from "@/types/auth.interface";
import type { IUser } from "@/types/(my-space)/users.interface";
import { setAuthTokensToCookie } from "@/utils/auth";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getLoginHistory", "getActiveSessions", "USER_PROFILE", "INITIATE_MFA", "VERIFY_MFA", "MFA_STATUS"],
  endpoints: (builder) => ({
    login: builder.mutation<IApiResponse<ILoginResponse>, ILoginPayload>({
      query: (body) => ({
        url: AUTH_LOGIN,
        method: "POST",
        body,
      }),
      onQueryStarted: async (_body, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { accessToken, refreshToken, deviceId } = data.data;
          if (accessToken) setAuthTokensToCookie(accessToken, refreshToken, deviceId, _body.rememberMe, _body.email);
        } catch (e: any) {
          console.error(e?.message ?? e?.data?.message ?? "Something went wrong. Please try again later.");
        }
      },
    }),
    loginMFA: builder.mutation<IApiResponse<ILoginWithMFAResponse>, IVerifyMFAPayload>({
      query: (body) => ({
        url: AUTH_VERIFY_MFA_LOGIN,
        method: "POST",
        body,
      }),
      onQueryStarted: async (_body, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { accessToken, refreshToken, deviceId } = data.data;
          if (accessToken && deviceId) setAuthTokensToCookie(accessToken, refreshToken, deviceId);
        } catch (e: any) {
          console.error(e?.message ?? e?.data?.message ?? "Something went wrong. Please try again later.");
        }
      },
    }),
    getAuthUserProfile: builder.query<IApiResponse<IUser>, void>({
      query: () => ({
        url: "/auth/profile",
        method: "GET",
        // This query gates every authenticated screen. A stopped API can hold
        // the socket open without answering, which would hang the boot spinner
        // forever — cap it so the failure surfaces as TIMEOUT_ERROR and the auth
        // check can route to /connection-lost instead of waiting.
        timeout: 10_000,
      }),
      providesTags: ["USER_PROFILE"],
    }),
    signUp: builder.mutation({
      query: (body: ISignUp) => ({
        url: AUTH_SIGN_UP,
        method: "POST",
        body,
      }),
    }),
    verifySignupEmail: builder.mutation<
      IApiResponse<ISignupVerifyEmailResponse>,
      { otp: string; email: string; accessToken: string }
    >({
      query: (body: IVerifyEmail) => ({
        url: AUTH_VERIFY_EMAIL,
        method: "POST",
        body,
      }),
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: AUTH_FORGOT_PASSWORD,
        method: "POST",
        body,
      }),
    }),
    verifyResetPasswordOTP: builder.mutation({
      query: (body) => ({
        url: AUTH_VERIFY_RESET_PASSWORD_OTP,
        method: "POST",
        body,
      }),
    }),
    resendOtp: builder.mutation({
      query: (body) => ({
        url: AUTH_RESEND_OTP,
        method: "POST",
        body,
      }),
    }),
    resetPassword: builder.mutation<IApiResponse<void>, { passwordToken: string; newPassword: string }>({
      query: (body) => ({
        url: AUTH_RESET_PASSWORD,
        method: "POST",
        body,
      }),
    }),
    validatePasswordResetToken: builder.query<IApiResponse<{ valid: boolean }>, string>({
      query: (passwordResetToken) => ({
        url: `${AUTH_VALIDATE_RESET_TOKEN}?passwordResetToken=${encodeURIComponent(passwordResetToken)}`,
        method: "GET",
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: AUTH_LOGOUT,
        method: "POST",
      }),
    }),
    getLoginHistory: builder.query<IApiResponse<IDeviceData[]>, void>({
      query: () => ({
        url: AUTH_LOGIN_HISTORY,
        method: "GET",
      }),
      providesTags: ["getLoginHistory"],
    }),
    getActiveSessions: builder.query<IApiResponse<IDeviceData[]>, void>({
      query: () => ({
        url: AUTH_ACTIVE_SESSIONS,
        method: "GET",
      }),
      providesTags: ["getActiveSessions"],
    }),
    initiateMFASetup: builder.query<IApiResponse<IInitiateMFAResponse>, void>({
      query: () => ({
        url: INITIATE_MFA_SETUP,
        method: "POST",
      }),
      providesTags: ["INITIATE_MFA"],
    }),
    verifyAndEnableMFA: builder.query<IApiResponse<TVerifyandEnableMFAResponse>, { mfaCode: string }>({
      // Usage of 'query' for PUT request is intentional, for using of cached response data in next step.
      query: ({ mfaCode }) => ({
        url: VERIFY_MFA_CODE,
        method: "PUT",
        body: { otp: mfaCode },
      }),
      providesTags: ["VERIFY_MFA"],
    }),
    disableMFA: builder.mutation<IApiResponse<void>, { mfaCode: string }>({
      query: ({ mfaCode }) => ({
        url: DISABLE_MFA,
        method: "PUT",
        body: { otp: mfaCode },
      }),
    }),
    getMFAStatus: builder.query<IApiResponse<IMFAStatusResponse>, void>({
      query: () => ({
        url: MFA_STATUS,
        method: "GET",
      }),
      providesTags: ["MFA_STATUS"],
    }),
    terminateSessionById: builder.mutation<{ success: boolean; message: string }, number>({
      query: (sessionId) => ({
        url: `${AUTH_TERMINATE}?sessionId=${sessionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getActiveSessions", "getLoginHistory"],
    }),
    terminateSessionExceptCurrent: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: AUTH_TERMINATE_OTHERS,
        method: "DELETE",
      }),
      invalidatesTags: ["getActiveSessions", "getLoginHistory"],
    }),
    exchangeImpersonationCode: builder.mutation<
      IApiResponse<{
        accessToken: string;
        refreshToken: string;
        deviceId: string;
        user: {
          id: number;
          email: string;
          firstName: string;
          lastName: string;
          companyId: number;
        };
        impersonation: {
          impersonationId: string;
          adminEmail: string;
          adminName: string;
          startedAt: string;
          expiresAt: string;
        };
      }>,
      { code: string }
    >({
      query: (body) => ({
        url: AUTH_EXCHANGE_IMPERSONATION_CODE,
        method: "POST",
        body,
      }),
    }),
    endImpersonation: builder.mutation<
      IApiResponse<{ redirectUrl: string }>,
      void
    >({
      query: () => ({
        url: AUTH_END_IMPERSONATION,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useValidatePasswordResetTokenQuery,
  useSignUpMutation,
  useVerifySignupEmailMutation,
  useGetAuthUserProfileQuery,
  useLoginMutation,
  useLoginMFAMutation,
  useForgotPasswordMutation,
  useResendOtpMutation,
  useVerifyResetPasswordOTPMutation,
  useResetPasswordMutation,
  useLogoutMutation,
  useGetLoginHistoryQuery,
  useGetActiveSessionsQuery,
  useInitiateMFASetupQuery,
  useLazyInitiateMFASetupQuery,
  useVerifyAndEnableMFAQuery,
  useLazyVerifyAndEnableMFAQuery,
  useGetMFAStatusQuery,
  useDisableMFAMutation,
  useTerminateSessionByIdMutation,
  useTerminateSessionExceptCurrentMutation,
  useExchangeImpersonationCodeMutation,
  useEndImpersonationMutation,
} = authApi;
